// src/AboutMe.jsx
import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';

// ─────────────────────────────────────────────
// RingSphere (unchanged from your version)
// ─────────────────────────────────────────────
function RingSphere({
  rings = 12,
  minRadius = 0.6,
  maxRadius = 3,
  height = 5,
  ringThickness = 0.12,
  color = 'rgba(206, 224, 237, 1)',
  opacity = 1,
  rotateSpeed = 0.25,
  animSpeed = 0.25,
  profileExponent = 1.2,
  position = [0, 0, 0],
}) {
  const groupRef = useRef();
  const tRef = useRef(0);

  const ringGeometry = useMemo(
    () =>
      new THREE.RingGeometry(
        1 - ringThickness / 2,
        1 + ringThickness / 2,
        64,
        1
      ),
    [ringThickness]
  );

  const bottomColor = useMemo(
    () => new THREE.Color('rgba(212, 251, 255, 1)'),
    []
  );
  const topColor = useMemo(
    () => new THREE.Color('rgba(101, 149, 183, 1)'),
    []
  );

  useFrame((_, dt) => {
    if (!groupRef.current) return;

    tRef.current += dt * animSpeed;

    if (rotateSpeed !== 0) {
      groupRef.current.rotation.y += dt * rotateSpeed;
    }

    const children = groupRef.current.children;

    children.forEach((ringMesh, i) => {
      const progress = (tRef.current + i / rings) % 1;
      const tt = progress * 2 - 1;

      const y = tt * (height / 2);
      ringMesh.position.y = y;

      const underRoot = Math.max(0, 1 - tt * tt);
      const baseFactor = Math.sqrt(underRoot);
      const sphereFactor = Math.pow(baseFactor, profileExponent);

      const newRadius =
        minRadius + (maxRadius - minRadius) * sphereFactor;

      ringMesh.scale.set(newRadius, newRadius, 1);

      const colorT = THREE.MathUtils.clamp((tt + 1) / 2, 0, 1);

      const mat = ringMesh.material;
      if (mat && mat.color) {
        mat.color.lerpColors(bottomColor, topColor, colorT);
        mat.opacity = opacity;
        mat.transparent = opacity < 1;
      }
    });
  });

  const indices = useMemo(
    () => Array.from({ length: rings }, (_, i) => i),
    [rings]
  );

  return (
    <group ref={groupRef} position={position}>
      {indices.map((i) => (
        <mesh
          key={i}
          geometry={ringGeometry}
          rotation={[Math.PI / 2, 0, 0]}
        >
          <meshStandardMaterial
            color={bottomColor}
            opacity={opacity}
            transparent={opacity < 1}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

// ─────────────────────────────────────────────
// AboutMe scene with proper ring repulsion
// ─────────────────────────────────────────────
export default function AboutMe({ pointer }) {
  const camRef = useRef();
  const ringGroupRef = useRef();

  // base world-space position for the ring group
  const baseWorldPos = useMemo(
    () => new THREE.Vector3(1.4, 3.6, 4),
    []
  );

  // temp vector reused for projection
  const tmpVec = useMemo(() => new THREE.Vector3(), []);

  // ✅ hook used at top level only
  const { camera, size } = useThree();

  useFrame(() => {
    if (!ringGroupRef.current) return;

    const { width, height } = size;

    // 1) Project base world position → NDC → screen pixels
    const ndc = tmpVec.copy(baseWorldPos).project(camera);

    const anchorX = (ndc.x * 0.5 + 0.5) * width;
    const anchorY = (-ndc.y * 0.5 + 0.5) * height; // NDC Y is flipped

    // 2) Pointer position (fallback to anchor)
    const px = pointer?.x ?? anchorX;
    const py = pointer?.y ?? anchorY;

    // vector from pointer → anchor (screen space)
    const dx = anchorX - px;
    const dy = anchorY - py;

    const dist = Math.sqrt(dx * dx + dy * dy) || 1;

    // 3) Repulsion strength based on distance
    const maxRadius = 800; // px: radius of effect
    const clamped = Math.min(dist, maxRadius);
    const strength = 1 - clamped / maxRadius; // 1 near, 0 far

    const dirX = dx / dist;
    const dirY = dy / dist;

    // 4) Convert screen repulsion → world-space motion
    const intensity3D = { x: 0.9, y: 0.6 }; // tweak for more/less motion

    const targetX =
      baseWorldPos.x + dirX * strength * intensity3D.x;

    // 🔥 IMPORTANT: flip Y when mapping screen → world
    // screen Y: down is +; world Y: up is +
    const targetY =
      baseWorldPos.y - dirY * strength * intensity3D.y;

    // 5) Smooth interpolation
    const pos = ringGroupRef.current.position;
    pos.x = THREE.MathUtils.lerp(pos.x, targetX, 0.08);
    pos.y = THREE.MathUtils.lerp(pos.y, targetY, 0.08);
  });

  return (
    <>
      <PerspectiveCamera
        ref={camRef}
        makeDefault
        position={[0, 4, 10]}
        fov={45}
        near={0.1}
        far={100}
      />
      <ambientLight intensity={0.5} color={'#fff'} />
        <directionalLight
          position={[5, 10, 2]}
          intensity={6}
          color="#f2feff"
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-near={1}
          shadow-camera-far={40}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
          shadow-bias={-0.0005}
        />

      <group
        ref={ringGroupRef}
        rotation={[Math.PI / 10, 0, -Math.PI / 8]}
        scale={[0.5, 0.5, 0.5]}
        position={[1.4, 3.6, 4]} // matches baseWorldPos
      >
        <RingSphere
          position={[0, 0, 0]}
          rings={8}
          minRadius={1.2}
          maxRadius={3.2}
          height={5}
          rotateSpeed={0.3}
        />
      </group>
    </>
  );
}
